import {useState, useCallback, useMemo} from 'react';
import { useNavigate } from "react-router-dom";
import { useUpdateCaseInfo, useRaiseIntimation, useVerifyDocument,useUpdateOnSpotServices, useSendIntimationEmail, useUpdateCaseStatus,useAddDailyUpdate, useDailyUpdates, useEditDailyUpdate } from '../../../hooks/useWorkshopQueries';
import { SectionCard, Badge, formatDate, formatDateTime, formatINR,EmptyState,docBadge, sourceMeta, Spinner, Avatar,timeAgo, statusBadge, STATUS_LABELS  } from "../UI";
import {buildDraft, WaitingForSurveyor, SpotSurveyStatus, AfterSpotSurveyPanel, WorkshopSurveyPanel, SurveyCard, SectionLabel, NextStepCard, StatusBanner, OptionCard, DocRow, InsuranceInfoCard, SkipReasonForm, SurveysSkippedBanner } from "./InsuranceTabHelpers";
import { StepDismantlingApproval, StepDismantlingCompleted, StepSecondVisit, StepRevisedEstimate, StepSecondSurveyDone, StepFinalApproval, StepWorkStarted, StepTruckRepaired, StepReinspection, StepProforma,RepairTimeline } from "./Workshop/WorkshopTabHelpers";
import { ServiceSelector, ServiceCard, ServicesSummary, SERVICE_CATALOG } from "./On-SpotServices/ServiceHelper";
import LinkedServiceLauncher from "./On-SpotServices/LinkedServiceLauncher";
import {findLinked} from "./On-SpotServices/CaseServiceAdapters";
import { useUploadDocuments } from '../../../Pages/Customers/WorkshopDashboard/workshopHooks'; 

import { DOCUMENT_FIELDS, DRIVER_DOC_FIELDS, HELPER_DOC_FIELDS, FIR_DOC_FIELD, LR_DOC_FIELD, ACCIDENT_IMAGE_FIELDS } from '../../../Pages/Customers/WorkshopDashboard/workshopConstants'; // adjust path


// ADDED: Modal imported for the new DocPreviewModal in DocumentsTab
import { Modal } from "../UI";
import CaseSLAPanel from "../SLA/CaseSLAPanel";
import { useEffect } from 'react';
import { useRef } from 'react';

const STAGE_ORDER = ["CASE_CREATED","SURVEY_PROCESS","WORKSHOP_REPAIR","DELIVERY_PAYMENT","CLAIM_SETTLEMENT"];
const stageIndex  = (stage) => STAGE_ORDER.indexOf(stage);

// ── Quick Stat ────────────────────────────────────────────────────────────────
export function QuickStat({ icon, label, value, pending, pendingColor = "var(--wd-t3)" }) {
  return (
    <div className="wd-card" style={{ padding:16 }}>
      <div style={{ fontSize:18, marginBottom:6 }}>{icon}</div>
      <div style={{ fontSize:11, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em", fontWeight:600 }}>{label}</div>
      {value
        ? <div style={{ fontSize:14, fontWeight:600, color:"var(--wd-t1)", marginTop:2 }}>{value}</div>
        : <div style={{ fontSize:12, color: pendingColor, marginTop:2, fontStyle:"italic" }}>{pending}</div>
      }
    </div>
  );
}

// Inline editable field — shows input in edit mode, InfoRow in view mode
function EditableField({ label, value, editValue, onChange, mono, type = "text", placeholder }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
      <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>
        {label}
      </span>
      {onChange !== undefined ? (
        <input
          className={`wd-input${mono ? " wd-mono" : ""}`}
          style={{ fontSize:13, padding:"5px 8px", height:30 }}
          type={type}
          value={editValue ?? ""}
          placeholder={placeholder || "—"}
          onChange={e => onChange(e.target.value)}
        />
      ) : (
        <span style={{ fontSize:13, color:"var(--wd-t1)", fontFamily: mono ? "var(--wd-mono)" : "var(--wd-sans)" }}>
          {value || "—"}
        </span>
      )}
    </div>
  );
}
 
// Edit / Save / Cancel button group shown in SectionCard action slot
function EditActions({ editing, saving, onEdit, onSave, onCancel }) {
  if (!editing) {
    return (
      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onEdit}>
        ✏️ Edit
      </button>
    );
  }
  return (
    <div style={{ display:"flex", gap:6 }}>
      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onCancel} disabled={saving}>
        Cancel
      </button>
      <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={onSave} disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
 
// ─── Main component ───────────────────────────────────────────────────────────
export function OverviewTab({ detail }) {
  console.log("detail--------------------------------------:", detail);
  const { caseInfo, driverDetails, accidentDetails, insuranceDetails } = detail;
  const mutation = useUpdateCaseInfo(caseInfo.accidentCaseNo);
 
  // ── Customer & Vehicle section ──────────────────────────────────────────────
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [customerForm, setCustomerForm] = useState({});

  const [editingDriver, setEditingDriver] = useState(false);
  const [driverForm, setDriverForm] = useState({});

  const [customerFormErrors, setCustomerFormErrors] = useState({});
  const [driverFormErrors, setDriverFormErrors] = useState({});
 
  const startEditCustomer = () => {
    setCustomerForm({
      customerEmail:    caseInfo.customerEmail    || "",
      customerPhone:    caseInfo.customerPhone    || "",
      customerName:     caseInfo.customerName     || "",
      vehicleNo:        caseInfo.vehicleNo        || "",
      vehicleMake:      accidentDetails?.vehicleMake      || "",
      accidentPincode:  caseInfo.accidentPincode  || "",
    });
    setEditingCustomer(true);
  };
 
  const saveCustomer = useCallback(async () => {
    const errs = {};
  if (customerForm.customerPhone && !/^\d{10}$/.test(customerForm.customerPhone)) {
    errs.customerPhone = "Must be 10 digits";
  }
  if (Object.keys(errs).length) {
    setCustomerFormErrors(errs);
    return;
  }
  setCustomerFormErrors({});
    try {
      await mutation.mutateAsync(customerForm);
      setEditingCustomer(false);
    } catch {}
  }, [customerForm, mutation]);

  const startEditDriver = () => {
  setDriverForm({
    name:          driverDetails?.name          || "",
    phone:         driverDetails?.phone         || "",
    licenseNo:     driverDetails?.licenseNo     || "",
    licenseExpiry: driverDetails?.licenseExpiry? driverDetails.licenseExpiry.split("T")[0] : "",
  });
  setEditingDriver(true);
};

const saveDriver = useCallback(async () => {
  const errs = {};
  if (driverForm.phone && !/^\d{10}$/.test(driverForm.phone)) {
    errs.phone = "Must be 10 digits";
  }
  if (Object.keys(errs).length) {
    setDriverFormErrors(errs);
    return;
  }
  setDriverFormErrors({});
  try {
    await mutation.mutateAsync(driverForm);
    setEditingDriver(false);
  } catch {}
}, [driverForm, mutation]);
 
  // ── Accident Details section ────────────────────────────────────────────────
  const [editingAccident, setEditingAccident] = useState(false);
  const [accidentForm, setAccidentForm] = useState({});
 
  const startEditAccident = () => {
    setAccidentForm({
      location:       caseInfo.location       || "",
      accidentDate:   caseInfo.accidentDate   || "",   // "2026-04-26" ✓
      accidentTime:   caseInfo.accidentTime   || "", 
      accidentState:  caseInfo.accidentState  || "",
      description:    accidentDetails?.description     || "",
      policeReportNo: accidentDetails?.policeReportNo  || "",
      caseType:       caseInfo.caseType       || "",
      repairType:     caseInfo.repairType     || "",
    });
    setEditingAccident(true);
  };
 
  const saveAccident = useCallback(async () => {
    try {
      await mutation.mutateAsync(accidentForm);
      setEditingAccident(false);
    } catch {}
  }, [accidentForm, mutation]);
 
  // ── Insurance Details section ───────────────────────────────────────────────
  const [editingInsurance, setEditingInsurance] = useState(false);
  const [insuranceForm, setInsuranceForm] = useState({});
 
  const startEditInsurance = () => {
    setInsuranceForm({
      policyNo:          caseInfo.policyNo                       || "",
      insuranceCompany:  insuranceDetails?.company || caseInfo.insuranceCompany || "",
      claimNo:           insuranceDetails?.claimNo               || "",
      policyType:        insuranceDetails?.policyType            || "",
      expiryDate:        insuranceDetails?.expiryDate            || "",
      repName:           insuranceDetails?.surveyorAllocated     || "",
    });
    setEditingInsurance(true);
  };
 
  const saveInsurance = useCallback(async () => {
    try {
      await mutation.mutateAsync(insuranceForm);
      setEditingInsurance(false);
    } catch {}
  }, [insuranceForm, mutation]);
 
  const saving = mutation.isPending;
 
  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
 
      {/* ── Customer & Vehicle ──────────────────────────────────────────────── */}
      <SectionCard
        title="Customer & Vehicle"
        action={
          <EditActions
            editing={editingCustomer}
            saving={saving}
            onEdit={startEditCustomer}
            onSave={saveCustomer}
            onCancel={() => setEditingCustomer(false)}
          />
        }
      >
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <EditableField
            label="Email"
            value={caseInfo.customerEmail}
            editValue={customerForm.customerEmail}
            onChange={editingCustomer ? v => setCustomerForm(f => ({ ...f, customerEmail: v })) : undefined}
          />
          <EditableField
            label="Phone"
            value={caseInfo.customerPhone}
            editValue={customerForm.customerPhone}
            onChange={editingCustomer ? v => setCustomerForm(f => ({ ...f, customerPhone: v.replace(/\D/g,"").slice(0,10) })) : undefined}
            mono
          />
          <EditableField
            label="Name"
            value={caseInfo.customerName}
            editValue={customerForm.customerName}
            onChange={editingCustomer ? v => setCustomerForm(f => ({ ...f, customerName: v })) : undefined}
          />
          <EditableField
            label="Vehicle No"
            value={caseInfo.vehicleNo}
            editValue={customerForm.vehicleNo}
            onChange={editingCustomer ? v => setCustomerForm(f => ({ ...f, vehicleNo: v })) : undefined}
            mono
          />
          <EditableField
            label="Chassis No"
            value={caseInfo.vehicleChassisNo}
            mono
          />
          <EditableField
            label="Engine No"
            value={caseInfo.vehicleEngineNo}
            mono
          />
          <EditableField
            label="Make / Model"
            value={accidentDetails?.vehicleMake}
            editValue={customerForm.vehicleMake}
            onChange={editingCustomer ? v => setCustomerForm(f => ({ ...f, vehicleMake: v })) : undefined}
          />
          <EditableField
            label="Pincode"
            value={caseInfo.accidentPincode}
            editValue={customerForm.accidentPincode}
            onChange={editingCustomer ? v => setCustomerForm(f => ({ ...f, accidentPincode: v })) : undefined}
            mono
          />
        </div>
        {mutation.isError && editingCustomer && (
          <div style={{ fontSize:12, color:"var(--wd-red)", marginTop:8 }}>
            Save failed — please try again.
          </div>
        )}
      </SectionCard>
 
      {/* ── Accident Details ────────────────────────────────────────────────── */}
      <SectionCard
        title="Accident Details"
        action={
          <EditActions
            editing={editingAccident}
            saving={saving}
            onEdit={startEditAccident}
            onSave={saveAccident}
            onCancel={() => setEditingAccident(false)}
          />
        }
      >
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <EditableField
            label="Location"
            value={caseInfo.location}
            editValue={accidentForm.location}
            onChange={editingAccident ? v => setAccidentForm(f => ({ ...f, location: v })) : undefined}
          />
 
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <EditableField
              label="Date"
              value={formatDate(caseInfo.accidentDate)}
              editValue={accidentForm.accidentDate}
              onChange={editingAccident ? v => setAccidentForm(f => ({ ...f, accidentDate: v })) : undefined}
              type="date"
            />
            <EditableField
              label="Time"
              value={caseInfo.accidentTime || "—"}
              editValue={accidentForm.accidentTime}
              onChange={editingAccident ? v => setAccidentForm(f => ({ ...f, accidentTime: v })) : undefined}
              type="time"
            />
          </div>
 
          <EditableField
            label="State"
            value={caseInfo.accidentState}
            editValue={accidentForm.accidentState}
            onChange={editingAccident ? v => setAccidentForm(f => ({ ...f, accidentState: v })) : undefined}
          />

          {/* ── Case Type: Accidental / Repair → Maintenance / Breakdown ────── */}
          <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
            <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>
              Case Type
            </span>
            {editingAccident ? (
              <div style={{ display:"flex", gap:8 }}>
                <select
                  className="wd-input wd-select"
                  style={{ flex:1 }}
                  value={accidentForm.caseType || ""}
                  onChange={e => {
                    const val = e.target.value;
                    setAccidentForm(f => ({
                      ...f,
                      caseType: val,
                      repairType: val === "Repair" ? f.repairType : "",
                    }));
                  }}
                >
                  <option value="">Select type…</option>
                  <option value="Accidental">Accidental</option>
                  <option value="Repair">Repair</option>
                </select>
                {accidentForm.caseType === "Repair" && (
                  <select
                    className="wd-input wd-select"
                    style={{ flex:1 }}
                    value={accidentForm.repairType || ""}
                    onChange={e => setAccidentForm(f => ({ ...f, repairType: e.target.value }))}
                  >
                    <option value="">Select repair type…</option>
                    <option value="Maintenance">Maintenance</option>
                    <option value="Breakdown">Breakdown</option>
                  </select>
                )}
              </div>
            ) : (
              <span style={{ fontSize:13, color: caseInfo.caseType ? "var(--wd-t2)" : "var(--wd-t3)", fontStyle: caseInfo.caseType ? "normal" : "italic" }}>
                {caseInfo.caseType
                  ? `${caseInfo.caseType}${caseInfo.caseType === "Repair" && caseInfo.repairType ? " · " + caseInfo.repairType : ""}`
                  : "Not set"}
              </span>
            )}
          </div>

          <EditableField
            label="Source"
            value={caseInfo.source
              ? `${sourceMeta(caseInfo.source).icon} ${sourceMeta(caseInfo.source).label}`
              : "—"}
            // no editValue / onChange — always read-only
          />
 
          {/* Description — textarea in edit mode */}
          {(accidentDetails?.description || editingAccident) && (
            <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
              <span style={{ fontSize:11, color:"var(--wd-t3)", fontWeight:600, textTransform:"uppercase", letterSpacing:".05em" }}>
                Description
              </span>
              {editingAccident ? (
                <textarea
                  className="wd-input"
                  rows={3}
                  style={{ resize:"vertical", fontSize:13, maxHeight: 120, overflowY:"auto" }}
                  value={accidentForm.description}
                  placeholder="Describe how the accident happened…"
                  onChange={e => setAccidentForm(f => ({ ...f, description: e.target.value }))}
                />
              ) : (
                <p style={{ fontSize:13, color:"var(--wd-t2)", lineHeight:1.6, margin:0, maxHeight: 80, overflowY: "auto", wordBreak: "break-word" }}>
                  {accidentDetails.description}
                </p>
              )}
            </div>
          )}
 
          <EditableField
            label="FIR / GD No"
            value={accidentDetails?.policeReportNo}
            editValue={accidentForm.policeReportNo}
            onChange={editingAccident ? v => setAccidentForm(f => ({ ...f, policeReportNo: v })) : undefined}
            mono
          />
        </div>
        {mutation.isError && editingAccident && (
          <div style={{ fontSize:12, color:"var(--wd-red)", marginTop:8 }}>
            Save failed — please try again.
          </div>
        )}
      </SectionCard>
 
      {/* ── Insurance Details ───────────────────────────────────────────────── */}
      <SectionCard
        title="Insurance Details"
        action={
          editingInsurance ? (
            <EditActions
              editing
              saving={saving}
              onSave={saveInsurance}
              onCancel={() => setEditingInsurance(false)}
            />
          ) : (
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              {!insuranceDetails?.company && <PendingTag />}
              <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={startEditInsurance}>
                ✏️ Edit
              </button>
            </div>
          )
        }
      >
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <EditableField
            label="Policy No"
            value={caseInfo.policyNo}
            editValue={insuranceForm.policyNo}
            onChange={editingInsurance ? v => setInsuranceForm(f => ({ ...f, policyNo: v })) : undefined}
            mono
          />
          <EditableField
            label="Company"
            value={insuranceDetails?.company || caseInfo.insuranceCompany}
            editValue={insuranceForm.insuranceCompany}
            onChange={editingInsurance ? v => setInsuranceForm(f => ({ ...f, insuranceCompany: v })) : undefined}
          />
          <EditableField
            label="Claim No"
            value={insuranceDetails?.claimNo}
            editValue={insuranceForm.claimNo}
            onChange={editingInsurance ? v => setInsuranceForm(f => ({ ...f, claimNo: v })) : undefined}
            mono
          />
          <EditableField
            label="Policy Type"
            value={insuranceDetails?.policyType}
            editValue={insuranceForm.policyType}
            onChange={editingInsurance ? v => setInsuranceForm(f => ({ ...f, policyType: v })) : undefined}
          />
          <EditableField
            label="Expiry"
            value={formatDate(insuranceDetails?.expiryDate)}
            editValue={insuranceForm.expiryDate}
            onChange={editingInsurance ? v => setInsuranceForm(f => ({ ...f, expiryDate: v })) : undefined}
            type="date"
          />
          <EditableField
            label="Rep. Name"
            value={insuranceDetails?.surveyorAllocated}
            editValue={insuranceForm.repName}
            onChange={editingInsurance ? v => setInsuranceForm(f => ({ ...f, repName: v })) : undefined}
          />
        </div>
        {mutation.isError && editingInsurance && (
          <div style={{ fontSize:12, color:"var(--wd-red)", marginTop:8 }}>
            Save failed — please try again.
          </div>
        )}
      </SectionCard>
 
      {/* ── Driver Details — read-only (customer fills this) ────────────────── */}
      <SectionCard
        title="Driver Details"
        action={
        <EditActions
        editing={editingDriver}
        saving={saving}
        onEdit={startEditDriver}
        onSave={saveDriver}
        onCancel={() => setEditingDriver(false)
        }
    />}
      >
        {!editingDriver && !driverDetails?.name ? (
          <PendingSection
      message="Not submitted yet"
      subtext="Customer needs to log in and fill driver details."
    />
        ) : (
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <EditableField
        label="Name"
        value={driverDetails?.name}
        editValue={driverForm.name}
        onChange={editingDriver ? v => setDriverForm(f => ({ ...f, name: v })) : undefined}
      />
      <EditableField
        label="Phone"
        value={driverDetails?.phone}
        editValue={driverForm.phone}
        onChange={editingDriver ? v => setDriverForm(f => ({ ...f, phone: v.replace(/\D/g,"").slice(0,10) })) : undefined}
        mono
      />
      <EditableField
        label="License No"
        value={driverDetails?.licenseNo}
        editValue={driverForm.licenseNo}
        onChange={editingDriver ? v => setDriverForm(f => ({ ...f, licenseNo: v })) : undefined}
        mono
      />
      <EditableField
        label="License Expiry"
        value={formatDate(driverDetails?.licenseExpiry)}
        editValue={driverForm.licenseExpiry}
        onChange={editingDriver ? v => setDriverForm(f => ({ ...f, licenseExpiry: v })) : undefined}
        type="date"
      />
          </div>
        )}
          {mutation.isError && editingDriver && (
          <div style={{ fontSize: 12, color: "var(--wd-red)", marginTop: 8 }}>
            Save failed — please try again.
          </div>
        )}
      </SectionCard>
 
    </div>
  );
}

// ── ADDED: DOC_ICONS map — mirrors Documents.jsx so rows and modal show the ──
// ── same emoji icons and accent colours per document type.               ──────
const DOC_ICONS = {
  "RC":                       { icon: "🪪", color: "#3b82f6" },
  "Insurance Policy":         { icon: "📋", color: "#22c55e" },
  "Driver License":           { icon: "🪪", color: "#a855f7" },
  "Fitness Certificate":      { icon: "🩺", color: "#14b8a6" },
  "Permit":                   { icon: "📄", color: "#f97316" },
  "Accident Photo - Front":   { icon: "📸", color: "#eab308" },
  "Accident Photo - Rear":    { icon: "📸", color: "#eab308" },
  "Accident Photo - Left":    { icon: "📸", color: "#eab308" },
  "Accident Photo - Right":   { icon: "📸", color: "#eab308" },
  "Accident Photo - Top":     { icon: "📸", color: "#eab308" },
  "Accident Photo - Interior":{ icon: "📸", color: "#eab308" },
  "Accident Photo-Front":     { icon: "📸", color: "#eab308" }, // alt spelling in existing mock data
};
const docMeta = (type) => DOC_ICONS[type] || { icon: "📁", color: "#64748b" };

// ── ADDED: Local icon components ─────────────────────────────────────────────
// Defined here so DocumentsTab doesn't depend on anything from Documents.jsx.
function EyeIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

// ── ULIP badge variant ─────────────────────────────────────────────────────────
function ulipBadgeVariant(status) {
  return {
    VALID:           "green",
    EXPIRING_SOON:   "yellow",
    EXPIRED:         "red",
    MISMATCH:        "red",
    BLACKLISTED:     "red",
    NOT_APPLICABLE:  "gray",
  }[status] || "gray";
}


function expiryLabel(dateStr) {
  if (!dateStr) return null;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  if (diff < 0)  return { text: "Expired",       color: "var(--wd-red)"    };
  if (diff < 30) return { text: "Expiring soon", color: "var(--wd-orange)" };
  return null;
}

// ── DocPreviewModal ────────────────────────────────────────────────────────────
function DocPreviewModal({ doc, onClose, onApprove, onReject }) {
  if (!doc) return null;

  const meta = docMeta(doc.type);
  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileUrl || "");

  const expiryColor = (() => {
    if (!doc.ulipExpiryDate) return "var(--wd-t2)";
    const diff = (new Date(doc.ulipExpiryDate) - new Date()) / (1000 * 60 * 60 * 24);
    if (diff < 0)  return "var(--wd-red)";
    if (diff < 30) return "var(--wd-orange)";
    return "var(--wd-green)";
  })();

  const expLabel = expiryLabel(doc.ulipExpiryDate);
  const isPending = ["PENDING", null, undefined].includes(doc.verificationStatus);

  return (
    <Modal
      open={!!doc}
      onClose={onClose}
      title="Document Verification"
      maxWidth={500}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
          {doc.fileUrl && (
            <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="wd-btn wd-btn-ghost wd-btn-sm">
              <DownloadIcon /> View file
            </a>
          )}
          <button className="wd-btn wd-btn-ghost" onClick={onClose} style={{ marginLeft: "auto" }}>Close</button>
        </div>
      }
    >
      {/* Document image preview */}
      <div style={{
        height: 200, borderRadius: 10, overflow: "hidden",
        background: "var(--wd-hover)", border: "1px solid var(--wd-border)",
        display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 4,
      }}>
        {doc.fileUrl && isImage ? (
          <img
            src={doc.fileUrl}
            alt={doc.type}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        ) : doc.fileUrl ? (
          <iframe
            src={doc.fileUrl}
            title={doc.type}
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 40 }}>{meta.icon}</span>
            <span style={{ fontSize: 12, color: "var(--wd-t3)" }}>No file available</span>
          </div>
        )}
      </div>

      {/* Doc type header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0 8px" }}>
        <div style={{
          width: 40, height: 40, borderRadius: 9, flexShrink: 0,
          background: `${meta.color}18`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
        }}>
          {meta.icon}
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--wd-t1)" }}>
            {(doc.type || "").replace(/_/g, " ")}
          </div>
          <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 2 }}>
            Uploaded by {doc.uploadedBy || "—"}
          </div>
        </div>
        {/* Current verification status badge */}
        <div style={{ marginLeft: "auto" }}>
          <Badge variant={docBadge(doc.verificationStatus)}>
            {doc.verificationStatus || "PENDING"}
          </Badge>
        </div>
      </div>

      <div style={{ height: 1, background: "var(--wd-border)", margin: "4px 0 16px" }} />

      {/* ULIP verification fields */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

        {/* ULIP Verified */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>
            ULIP Verified
          </span>
          <span style={{ fontSize: 13, fontWeight: 600, color: doc.ulipStatus ? (["VALID","EXPIRING_SOON"].includes(doc.ulipStatus) ? "var(--wd-green)" : "var(--wd-red)") : "var(--wd-t3)" }}>
            {doc.ulipStatus
              ? (["VALID","EXPIRING_SOON"].includes(doc.ulipStatus) ? "Yes" : "No")
              : "Not verified yet"
            }
          </span>
        </div>

        {/* ULIP Status */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>
            ULIP Status
          </span>
          {doc.ulipStatus
            ? <Badge variant={ulipBadgeVariant(doc.ulipStatus)}>{doc.ulipStatus.replace(/_/g, " ")}</Badge>
            : <span style={{ fontSize: 13, color: "var(--wd-t3)", fontStyle: "italic" }}>Not verified yet</span>
          }
        </div>

        {/* Expiry Date */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: "var(--wd-t3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: ".05em" }}>
            Expiry Date
          </span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: expiryColor }}>
              {doc.ulipExpiryDate ? formatDate(doc.ulipExpiryDate) : "—"}
            </span>
            {expLabel && (
              <span style={{
                fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 6,
                background: `${expLabel.color}18`, color: expLabel.color,
                border: `1px solid ${expLabel.color}33`,
              }}>
                {expLabel.text}
              </span>
            )}
          </div>
        </div>

      </div>

      {/* Approve / Reject */}
      {isPending && (
        <>
          <div style={{ height: 1, background: "var(--wd-border)", margin: "16px 0" }} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              className="wd-btn wd-btn-sm"
              style={{ background: "rgba(34,197,94,.12)", color: "var(--wd-green)", border: "1px solid rgba(34,197,94,.25)", fontWeight: 600 }}
              onClick={() => { onApprove?.(doc); onClose(); }}
            >
              ✓ Approve
            </button>
            <button
              className="wd-btn wd-btn-danger wd-btn-sm"
              onClick={() => { onReject?.(doc); onClose(); }}
            >
              ✗ Reject
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function DocFileZone({ value, onChange, disabled }) {
  const ref = useRef(null);
  const filled = !!value;
  const fmtSize = (b) => b < 1048576 ? `${(b/1024).toFixed(0)} KB` : `${(b/1048576).toFixed(1)} MB`;
  return (
    <div
      onClick={() => !disabled && ref.current?.click()}
      style={{
        display:"flex", alignItems:"center", gap:7,
        minHeight:38, padding:"6px 9px", borderRadius:8, cursor: disabled ? "not-allowed" : "pointer",
        border: filled ? "1px solid rgba(99,102,241,.4)" : "1px solid var(--wd-border)",
        background: filled ? "rgba(99,102,241,.07)" : "rgba(255,255,255,.02)",
        transition:"border-color .12s, background .12s", opacity: disabled ? 0.5 : 1,
      }}
    >
      <input ref={ref} type="file" accept="image/*,application/pdf" disabled={disabled}
        onChange={e => onChange(e.target.files[0] || null)}
        style={{ position:"absolute", width:1, height:1, opacity:0, pointerEvents:"none" }} />
      <div style={{
        width:24, height:24, borderRadius:6, flexShrink:0,
        display:"flex", alignItems:"center", justifyContent:"center", fontSize:11,
        background: filled ? "rgba(99,102,241,.15)" : "rgba(148,163,184,.07)",
        border: filled ? "1px solid rgba(99,102,241,.25)" : "1px solid var(--wd-border)",
        color: filled ? "#818cf8" : "var(--wd-t3)",
      }}>
        {filled ? "✓" : "↑"}
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:11, fontWeight:600, color: filled ? "#a5b4fc" : "var(--wd-t3)",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>
          {filled ? `${value.name} · ${fmtSize(value.size)}` : "Choose or drop file"}
        </div>
      </div>
      {filled && (
        <button onClick={e => { e.stopPropagation(); onChange(null); }}
          style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"var(--wd-t3)", padding:"2px 4px" }}>✕</button>
      )}
    </div>
  );
}

function DocSectionHead({ icon, label, count }) {
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:8,
      paddingBottom:10, borderBottom:"1px solid var(--wd-border)", marginBottom:12,
    }}>
      <span style={{ fontSize:14 }}>{icon}</span>
      <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".06em", color:"var(--wd-t2)" }}>{label}</span>
      {count != null && (
        <span style={{ marginLeft:"auto", fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:10, background:"rgba(99,102,241,.1)", color:"#818cf8" }}>
          {count} fields
        </span>
      )}
    </div>
  );
}

function DocFieldRow({ label, docKey, files, onChange, existingUrl, disabled }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", fontSize:11, fontWeight:600, color:"var(--wd-t2)" }}>
        <span>{label}</span>
        {existingUrl && (
          <a href={existingUrl} target="_blank" rel="noreferrer"
            style={{ fontSize:10, fontWeight:600, color:"#7dd3fc", padding:"1px 6px", borderRadius:5, background:"rgba(125,211,252,.07)", border:"1px solid rgba(125,211,252,.15)" }}>
            ↗ View current
          </a>
        )}
      </div>
      <DocFileZone value={files[docKey] || null} onChange={f => onChange(docKey, f)} disabled={disabled} />
    </div>
  );
}

function UploadDocsModal({ open, onClose, caseId, existingDocuments = [], onSuccess }) {
  const { mutateAsync: uploadDoc } = useUploadDocuments();
  const [files,     setFiles]     = useState({});
  const [showHelper,setShowHelper]= useState(false);
  const [showFir,   setShowFir]   = useState(false);
  const [showLr,    setShowLr]    = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState({ current:0, total:0 });
  const [errors,    setErrors]    = useState([]);
  const [done,      setDone]      = useState(false);

  useEffect(() => {
    if (open) { setFiles({}); setErrors([]); setDone(false); setProgress({ current:0, total:0 }); setShowHelper(false); setShowFir(false); setShowLr(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !uploading) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, uploading, onClose]);

  const existingByKey = Object.fromEntries(
    existingDocuments.filter(d => (d?.documentType || d?.type) && d?.fileUrl)
      .map(d => [(d.documentType || d.type).toLowerCase(), d.fileUrl])
  );

  const setFile = useCallback((key, file) => setFiles(p => ({ ...p, [key]: file })), []);

  const buildList = () => {
    const list = [], push = (key) => { if (files[key]) list.push({ documentType:key, file:files[key] }); };
    DRIVER_DOC_FIELDS.forEach(({ key }) => push(key));
    DOCUMENT_FIELDS.forEach(({ key }) => push(key));
    if (showHelper) HELPER_DOC_FIELDS.forEach(({ key }) => push(key));
    if (showFir) push(FIR_DOC_FIELD.key);
    if (showLr)  push(LR_DOC_FIELD.key);
    ACCIDENT_IMAGE_FIELDS.forEach(({ key }) => push(key));
    return list;
  };

  const count = buildList().length;

  const handleUpload = async () => {
    const list = buildList();
    if (!list.length) return;
    setUploading(true); setErrors([]);
    setProgress({ current:0, total:list.length });
    const errs = [];
    for (let i = 0; i < list.length; i++) {
      setProgress({ current:i+1, total:list.length });
      try { await uploadDoc({ caseId, documentType:list[i].documentType, file:list[i].file }); }
      catch (err) { errs.push(`${list[i].documentType}: ${err?.message || "failed"}`); }
    }
    setUploading(false); setErrors(errs);
    if (!errs.length) { setDone(true); setTimeout(() => onSuccess?.(), 900); }
  };

  if (!open) return null;

  const pill = (label, state, set) => (
    <button key={label} onClick={() => set(p => !p)} disabled={uploading}
      style={{
        fontSize:11, fontWeight:600, padding:"4px 11px", borderRadius:20, cursor:"pointer",
        background: state ? "rgba(99,102,241,.12)" : "rgba(148,163,184,.07)",
        color: state ? "#a5b4fc" : "var(--wd-t2)",
        border: state ? "1px solid rgba(99,102,241,.25)" : "1px solid var(--wd-border)",
        transition:"all .12s",
      }}>
      {state ? "✓ " : ""}{label}
    </button>
  );

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget && !uploading) onClose(); }}
      style={{ position:"fixed", inset:0, zIndex:1200, background:"rgba(0,0,0,.65)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:16 }}
    >
      <div style={{ width:"100%", maxWidth:640, maxHeight:"90vh", display:"flex", flexDirection:"column", borderRadius:16, background:"var(--wd-surface,#111827)", border:"1px solid rgba(99,102,241,.18)", boxShadow:"0 24px 72px rgba(0,0,0,.7)", overflow:"hidden" }}>

        {/* header */}
        <div style={{ display:"flex", alignItems:"center", gap:12, padding:"16px 20px", borderBottom:"1px solid rgba(255,255,255,.05)", flexShrink:0 }}>
          <div style={{ width:36, height:36, borderRadius:9, background:"rgba(99,102,241,.12)", border:"1px solid rgba(99,102,241,.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>📎</div>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>Upload Documents</div>
            <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:1 }}>Case {caseId} — choose files per section</div>
          </div>
          {!uploading && (
            <button onClick={onClose} style={{ background:"none", border:"none", cursor:"pointer", fontSize:16, color:"var(--wd-t3)", padding:4, lineHeight:1, borderRadius:6 }}>✕</button>
          )}
        </div>

        {/* body */}
        <div style={{ overflowY:"auto", flex:1, padding:"18px 20px", display:"flex", flexDirection:"column", gap:20 }}>
          {done ? (
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10, padding:"40px 0", textAlign:"center" }}>
              <div style={{ width:52, height:52, borderRadius:"50%", background:"rgba(34,197,94,.1)", border:"1px solid rgba(34,197,94,.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>✓</div>
              <div style={{ fontSize:14, fontWeight:700, color:"var(--wd-t1)" }}>All documents uploaded</div>
              <div style={{ fontSize:12, color:"var(--wd-t3)" }}>Closing…</div>
            </div>
          ) : (<>
            {/* optional section toggles */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", alignItems:"center", padding:"10px 12px", background:"rgba(255,255,255,.02)", border:"1px solid rgba(255,255,255,.05)", borderRadius:10 }}>
              <span style={{ fontSize:11, fontWeight:600, textTransform:"uppercase", letterSpacing:".04em", color:"var(--wd-t3)" }}>Optional sections</span>
              {pill("Helper docs", showHelper, setShowHelper)}
              {pill("FIR document", showFir, setShowFir)}
              {pill("LR document", showLr, setShowLr)}
            </div>

            {/* driver */}
            <div>
              <DocSectionHead icon="🪪" label="Driver Documents" count={DRIVER_DOC_FIELDS.length} />
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
                {DRIVER_DOC_FIELDS.map(({ key, label }) => <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />)}
              </div>
            </div>

            {/* vehicle */}
            <div>
              <DocSectionHead icon="🚛" label="Vehicle Documents" count={DOCUMENT_FIELDS.length} />
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
                {DOCUMENT_FIELDS.map(({ key, label }) => <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />)}
              </div>
            </div>

            {/* helper (conditional) */}
            {showHelper && (
              <div>
                <DocSectionHead icon="👥" label="Co-Pilot / Helper Documents" count={HELPER_DOC_FIELDS.length} />
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
                  {HELPER_DOC_FIELDS.map(({ key, label }) => <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />)}
                </div>
              </div>
            )}

            {/* FIR (conditional) */}
            {showFir && (
              <div>
                <DocSectionHead icon="🚨" label="FIR / GD Document" />
                <div style={{ maxWidth:200 }}>
                  <DocFieldRow label={FIR_DOC_FIELD.label} docKey={FIR_DOC_FIELD.key} files={files} onChange={setFile} existingUrl={existingByKey[FIR_DOC_FIELD.key.toLowerCase()]} disabled={uploading} />
                </div>
              </div>
            )}

            {/* LR (conditional) */}
            {showLr && (
              <div>
                <DocSectionHead icon="📦" label="LR / Loading Receipt" />
                <div style={{ maxWidth:200 }}>
                  <DocFieldRow label={LR_DOC_FIELD.label} docKey={LR_DOC_FIELD.key} files={files} onChange={setFile} existingUrl={existingByKey[LR_DOC_FIELD.key.toLowerCase()]} disabled={uploading} />
                </div>
              </div>
            )}

            {/* accident images */}
            <div>
              <DocSectionHead icon="📸" label="Accident Images" count={ACCIDENT_IMAGE_FIELDS.length} />
              <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))", gap:10 }}>
                {ACCIDENT_IMAGE_FIELDS.map(({ key, label }) => <DocFieldRow key={key} label={label} docKey={key} files={files} onChange={setFile} existingUrl={existingByKey[key.toLowerCase()]} disabled={uploading} />)}
              </div>
            </div>

            {/* errors */}
            {errors.length > 0 && (
              <div style={{ padding:"12px 14px", borderRadius:10, background:"rgba(239,68,68,.07)", border:"1px solid rgba(239,68,68,.2)" }}>
                <div style={{ fontSize:12, fontWeight:700, color:"var(--wd-red)", marginBottom:6 }}>⚠️ Some uploads failed:</div>
                {errors.map((e, i) => <div key={i} style={{ fontSize:12, color:"#fca5a5", marginTop:2 }}>{e}</div>)}
              </div>
            )}

            {/* progress */}
            {uploading && (
              <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, fontWeight:600, color:"#818cf8" }}>
                  <span>Uploading {progress.current} of {progress.total}…</span>
                  <span>{progress.total ? Math.round((progress.current/progress.total)*100) : 0}%</span>
                </div>
                <div style={{ height:4, borderRadius:99, background:"rgba(99,102,241,.15)" }}>
                  <div style={{ height:"100%", borderRadius:99, background:"var(--wd-indigo,#6366f1)", width:`${progress.total ? Math.round((progress.current/progress.total)*100) : 0}%`, transition:"width .2s ease" }} />
                </div>
              </div>
            )}
          </>)}
        </div>

        {/* footer */}
        {!done && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 20px", borderTop:"1px solid rgba(255,255,255,.05)", flexShrink:0 }}>
            <span style={{ fontSize:11, color: count > 0 ? "#818cf8" : "var(--wd-t3)" }}>
              {count > 0 ? `📎 ${count} file${count !== 1 ? "s" : ""} ready to upload` : "No files selected yet"}
            </span>
            <div style={{ display:"flex", gap:8 }}>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onClose} disabled={uploading}>Cancel</button>
              <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={handleUpload} disabled={uploading || count === 0}
                style={{ opacity: count === 0 ? 0.4 : 1, cursor: count === 0 ? "not-allowed" : "pointer" }}>
                {uploading ? "Uploading…" : `⬆ Upload${count > 0 ? ` (${count})` : ""}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
// ── Documents Tab ─────────────────────────────────────────────────────────────
export function DocumentsTab({ documents, caseInfo }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const docs = documents || [];
  const isBeforeDocStage = ["CASE_REGISTERED","CUSTOMER_REGISTERED"].includes(caseInfo.currentStatus);

  const userRole = localStorage.getItem("department") || "";   // ← add
  const isKAM = userRole.toLowerCase() === "kam"; 

  const verifyDoc = useVerifyDocument();
  const [previewDoc, setPreviewDoc] = useState(null);
  const [actionError, setActionError] = useState(null);

  const handleApprove = async (doc) => {
    if (!doc?.id || isKAM) return;
    setActionError(null);
    try {
      await verifyDoc.mutateAsync({ docId: doc.id, status: "APPROVED" });
      setPreviewDoc(null);
    } catch (err) {
      console.error("Approve doc failed", err);
      setActionError("Approve failed. Please retry.");
    }
  };

  const handleReject = async (doc) => {
    if (!doc?.id || isKAM) return;
    setActionError(null);
    try {
      await verifyDoc.mutateAsync({ docId: doc.id, status: "REJECTED", remarks: "Rejected from case detail" });
      setPreviewDoc(null);
    } catch (err) {
      console.error("Reject doc failed", err);
      setActionError("Reject failed. Please retry.");
    }
  };

  const uploadButton = (
    <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setUploadOpen(true)}>
      📎 Upload Documents
    </button>
  );

    const uploadModal = (
    <UploadDocsModal
      open={uploadOpen}
      onClose={() => setUploadOpen(false)}
      caseId={caseInfo?.accidentCaseNo}
      existingDocuments={docs}
      onSuccess={() => setUploadOpen(false)}
    />
  );

  if (docs.length === 0) {
    return (
      <>
      <div className="wd-card" style={{ padding:32 }}>
        <PendingSection icon="📄" message="No documents uploaded yet"
          subtext={isBeforeDocStage
            ? "Customer has not logged in yet. They will receive a portal link via email to upload documents."
            : "Customer has logged in but hasn't uploaded documents yet."}
          badge={isBeforeDocStage ? "Awaiting customer login" : "Awaiting upload"}
          badgeColor="var(--wd-orange)"
        />
          <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
           {uploadButton}
          </div>
      </div>
      {uploadModal}
      </>
    );
  }
 
  const pending  = docs.filter(d => d.status === "PENDING").length;
  const approved = docs.filter(d => ["APPROVED","VERIFIED"].includes(d.status)).length;
  const rejected = docs.filter(d => d.status === "REJECTED").length;
 
  return (
    // ADDED: Fragment wrapper so DocPreviewModal can sit alongside the existing div
    <>
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        <div style={{ display:"flex", gap:12 }}>
          {[["Total",docs.length,"#3b82f6"],["Approved",approved,"#22c55e"],["Pending",pending,"#eab308"],["Rejected",rejected,"#ef4444"]].map(([l,v,c]) => (
            <div key={l} style={{ padding:"8px 16px", borderRadius:10, background:`${c}18`, border:`1px solid ${c}33`, display:"flex", alignItems:"center", gap:8 }}>
              <span style={{ fontSize:18, fontWeight:800, color:c }}>{v}</span>
              <span style={{ fontSize:12, color:"var(--wd-t2)" }}>{l}</span>
            </div>
          ))}
          {uploadButton}
        </div>
        {pending > 0 && (
          <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(234,179,8,.08)", borderRadius:10, border:"1px solid rgba(234,179,8,.2)" }}>
            <span>⚠️</span>
            <span style={{ fontSize:12, color:"var(--wd-yellow)" }}><strong>{pending} document(s)</strong> waiting for your review.</span>
          </div>
        )}
        {rejected > 0 && (
          <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(239,68,68,.08)", borderRadius:10, border:"1px solid rgba(239,68,68,.2)" }}>
            <span>❌</span>
            <span style={{ fontSize:12, color:"var(--wd-red)" }}><strong>{rejected} document(s)</strong> rejected — customer needs to re-upload.</span>
          </div>
        )}
        <div className="wd-card">
          <div className="wd-table-wrap">
            <table className="wd-table">
              <thead>
                <tr>
                  {/* CHANGED: TYPE column header kept; added icon rendering in cell below */}
                  <th>TYPE</th>
                  <th>STATUS</th>
                  <th>UPLOADED BY</th>
                  <th>EXPIRY DATE</th>
                  <th>ULIP VERIFIED</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => {
                  // ADDED: doc icon meta for the type cell
                  const meta = docMeta(doc.type);

                  return (
                    // CHANGED: Row is now clickable — opens DocPreviewModal for this doc.
                    //          cursor:pointer added to signal interactivity.
                    <tr key={doc.id}
                      style={{ cursor:"pointer" }}
                      onClick={() => setPreviewDoc(doc)}
                    >
                      <td>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <div style={{
                            width:34, height:34, borderRadius:8, flexShrink:0,
                            background:`${meta.color}18`,
                            display:"flex", alignItems:"center", justifyContent:"center", fontSize:16,
                          }}>
                            {meta.icon}
                          </div>
                          <span style={{ fontWeight:500, fontSize:13 }}>
                            {(doc.type || "").replace(/_/g, " ")}
                          </span>
                        </div>
                      </td>

                      <td><Badge variant={docBadge(doc.status)}>{doc.status || "PENDING"}</Badge></td>

                      <td style={{ fontSize:12, color:"var(--wd-t3)" }}>{doc.uploadedBy || "—"}</td>

                      <td style={{ fontSize:12, color: doc.ulipExpiryDate ? (() => {
                        const diff = (new Date(doc.ulipExpiryDate) - new Date()) / (1000 * 60 * 60 * 24);
                        return diff < 0 ? "var(--wd-red)" : "var(--wd-green)";
                      })() : "var(--wd-t3)" }}>
                        {doc.ulipExpiryDate ? formatDate(doc.ulipExpiryDate) : "—"}
                      </td>

                      <td style={{ fontSize:12, color:"var(--wd-t3)" }}>
                        {doc.ulipVerified === true ? "Yes" : doc.ulipVerified === false ? "No" : "Not verified yet"}
                      </td>

                      <td onClick={e => e.stopPropagation()}>
                        <div style={{ display:"flex", gap:5 }}>
                          <button
                            className="wd-btn wd-btn-ghost wd-btn-sm"
                            onClick={() => setPreviewDoc(doc)}
                          >
                            <EyeIcon /> View
                          </button>
                          {doc.fileUrl && (
                            <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer"
                              className="wd-btn wd-btn-ghost wd-btn-sm"
                              onClick={e => e.stopPropagation()}
                            >
                              <DownloadIcon />
                            </a>
                          )}
                          {["PENDING",null,undefined].includes(doc.status) && !isKAM && <>
                            <button className="wd-btn wd-btn-sm" style={{ background:"rgba(34,197,94,.12)", color:"var(--wd-green)", border:"none" }} onClick={() => handleApprove(doc)} disabled={verifyDoc.isLoading}>✓ Approve</button>
                            <button className="wd-btn wd-btn-danger wd-btn-sm" onClick={() => handleReject(doc)} disabled={verifyDoc.isLoading}>✗ Reject</button>
                          </>}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {actionError && (
        <div style={{ margin: "8px 0", color: "var(--wd-red)", fontSize: 12 }}>
          {actionError}
        </div>
      )}
      <DocPreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
        onApprove={handleApprove}
        onReject={handleReject}
      />
       {uploadModal}
    </>
  );
}

// ── Insurance Intimation Tab ─────────────────────────────────────────────────
export function InsuranceTab({ detail, onSuccess, onGoToSurveys }) {
  const [claimNoDeferred, setClaimNoDeferred] = useState(false);

  const navigate = useNavigate();

  const { caseInfo, insuranceDetails, accidentDetails, documents,surveys } = detail || {};
  const raiseIntimation = useRaiseIntimation();
  const sendIntimationEmail = useSendIntimationEmail();
  const updateStatus = useUpdateCaseStatus();
  const updateCaseInfo = useUpdateCaseInfo(caseInfo?.accidentCaseNo);

  const alreadyRaised  = !!caseInfo?.intimationRaised;   // adapt field name to your schema
  const intimationDate = caseInfo?.intimationDate;
  const intimationMode = caseInfo?.intimationMode;

  const intimationSkipped   = !!caseInfo?.intimationSkipped;
  const intimationSkipReason = caseInfo?.intimationSkipReason;
  const surveySkipped       = !!caseInfo?.surveySkipped;
  const surveySkipReason    = caseInfo?.surveySkipReason;

  const intimationHandled = alreadyRaised || intimationSkipped;

  const spotSurvey = surveys?.find(s =>
  (s.type || "").toLowerCase().includes("spot")
  );
  const surveyorAssigned = !!spotSurvey?.surveyorId;

  // view: null | "choose" | "direct" | "email"
  const [view, setView]         = useState(null);
  const [saving, setSaving]     = useState(false);
  const [success, setSuccess]   = useState(null); // null | string (message)
  const [successType, setSuccessType] = useState("raised"); // "raised" | "skipped"

  // ── Direct form state
  const [directMethod, setDirectMethod] = useState("TELEPHONIC");
  const [directDate,   setDirectDate]   = useState(new Date().toISOString().slice(0, 10));
  const [directRef,    setDirectRef]    = useState("");
  const [directNote,   setDirectNote]   = useState("");
  const [directTP,     setDirectTP]     = useState(false);

  // ── Email form state
  const docs = useMemo(() => documents || [], [documents]);
  const [selectedDocs, setSelectedDocs] = useState(() =>
    Object.fromEntries(docs.map(d => [d.id, true]))
  );
  const [emailTo,      setEmailTo]      = useState(insuranceDetails?.email || caseInfo?.insuranceEmail || "");
  const [emailCc,      setEmailCc]      = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailTP,      setEmailTP]      = useState(false);
  const [emailBody,    setEmailBody]    = useState("");

  // Helper: extract subject from draft (first line after "Subject: ")
  const extractSubjectFromDraft = (draft) => {
    const match = draft.match(/^Subject:\s*(.+?)\n/);
    return match ? match[1] : "";
  };

  // Helper: extract body from draft (everything after first blank line)
  const extractBodyFromDraft = (draft) => {
    const lines = draft.split("\n");
    const subjectIndex = lines.findIndex(l => l.startsWith("Subject:"));
    if (subjectIndex === -1) return draft;
    // Skip subject line and the first blank line
    return lines.slice(subjectIndex + 2).join("\n").trim();
  };

  const selectedCount = Object.values(selectedDocs).filter(Boolean).length;

  const toggleDoc = (id, val) => setSelectedDocs(p => ({ ...p, [id]: val }));

  const refreshDraft = (tp = emailTP) => {
    const fullDraft = buildDraft({
      caseInfo, insuranceDetails, accidentDetails,
      documents: docs.filter(d => selectedDocs[d.id]),
      thirdParty: tp,
    });
    setEmailSubject(extractSubjectFromDraft(fullDraft));
    setEmailBody(extractBodyFromDraft(fullDraft));
  };

  const openEmail = () => {
    const fullDraft = buildDraft({
      caseInfo, insuranceDetails, accidentDetails,
      documents: docs.filter(d => selectedDocs[d.id]),
      thirdParty: false,
    });
    setEmailSubject(extractSubjectFromDraft(fullDraft));
    setEmailBody(extractBodyFromDraft(fullDraft));
    setView("email");
  };

  // ── Submit: direct
  const handleDirectSave = async () => {
    setSaving(true);
    try {
      await raiseIntimation.mutateAsync({
        caseId: caseInfo?.accidentCaseNo,
        mode: directMethod,
        intimationDate: directDate,
        note: directNote,
        referenceNo: directRef,
        thirdPartyLoss: directTP,
      });
      setSuccessType("raised");  
      setSuccess(`Intimation logged as raised via ${directMethod}. Case status updated to INTIMATION_RAISED.`);
      onSuccess?.();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  // ── Submit: email
  const handleSendEmail = async () => {
    setSaving(true);
    try {
      // 1) Update case status to INTIMATION_RAISED (records intimation metadata)
      await raiseIntimation.mutateAsync({
        caseId: caseInfo?.accidentCaseNo,
        mode: "EMAIL",
        intimationDate: new Date().toISOString().slice(0, 10),
        note: `Intimation email sent to ${emailTo}`,
        referenceNo: null,
        thirdPartyLoss: emailTP,
      });

      // 2) Trigger backend event email (uses existing template / resolver on server)
      // Pass extraData so backend template can include the composed body and requested attachments
      await sendIntimationEmail.mutateAsync({
        caseId: caseInfo?.accidentCaseNo,
        to: emailTo,
        cc: emailCc,
        subject: emailSubject,
        body: emailBody,
        attachDocIds: Object.entries(selectedDocs).filter(([,v])=>v).map(([k])=>k),
      });
      setSuccess(`Intimation email sent to ${emailTo}. ${selectedCount} document(s) attached. Case status → INTIMATION_RAISED.`);
      onSuccess?.();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleSkipIntimation = async (reason) => {
  setSaving(true);
  try {
    await updateCaseInfo.mutateAsync({
      intimationSkipped: true,
      intimationSkipReason: reason,
    });
    setSuccessType("skipped");                         // ADDED
    setSuccess(`Intimation skipped. Reason recorded — you can still assign a spot surveyor whenever ready.`);
    onSuccess?.();
  } catch (err) { console.error(err); }
  setSaving(false);
};

  const handleSkipSurveys = async (reason) => {
    setSaving(true);
    try {
      await updateCaseInfo.mutateAsync({
        intimationSkipped: true,
        intimationSkipReason: caseInfo?.intimationSkipReason || reason,
        surveySkipped: true,
        surveySkipReason: reason,
      });
      await updateStatus.mutateAsync({
        caseId: caseInfo?.accidentCaseNo,
        status: "WORKSHOP_ASSIGNED",
        description: `Intimation & surveys skipped — ${reason}`,
        role: "ADMIN",
      });
      onSuccess?.();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const reset = () => { setView(null); setSuccess(null); };

  // ─────────────────────────────────────────────────────────────────────────
  // Success screen
  // ─────────────────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Confirmation strip */}
        <div style={{
          padding: "16px 18px", borderRadius: 12,
          background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.22)",
          display: "flex", alignItems: "flex-start", gap: 12,
        }}>
          <span style={{ fontSize: 22, flexShrink: 0 }}>✅</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, 
              color: successType === "skipped" ? "var(--wd-t2)" : "var(--wd-green)",
              marginBottom: 3 
              }}>
            {successType === "skipped" ? "Intimation Skipped" : "Intimation Raised Successfully"}            </div>
            <div style={{ fontSize: 12, color: "var(--wd-t2)", marginBottom: 8 }}>{success}</div>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20,
              background: successType === "skipped" ? "rgba(100,116,139,.1)" : "rgba(59,130,246,.1)",  
              color: successType === "skipped" ? "var(--wd-t3)" : "var(--wd-blue)",
              border: `1px solid ${successType === "skipped" ? "rgba(100,116,139,.2)" : "rgba(59,130,246,.2)"}`,
            }}>
            {successType === "skipped" ? "Status → Intimation Skipped" : "Status → INTIMATION_RAISED"}
            </span>
          </div>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ flexShrink: 0 }} onClick={reset}>← Back</button>
        </div>
        
        {successType !== "skipped" && (
        !claimNoDeferred ? (
          <div style={{
            padding: "16px 18px", borderRadius: 12,
            background: "rgba(234,179,8,.06)", border: "1px solid rgba(234,179,8,.22)",
            display: "flex", alignItems: "flex-start", gap: 14,
          }}>
            <span style={{ fontSize: 22, flexShrink: 0 }}>📋</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-yellow)", marginBottom: 4 }}>
                Add Claim Number
              </div>
              <div style={{ fontSize: 12, color: "var(--wd-t2)", lineHeight: 1.6, marginBottom: 10 }}>
                Insurance companies usually send a claim number within 24–48 hours via email or SMS.
                You can add it now if you already have it, or come back later.
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  className="wd-btn wd-btn-sm"
                  style={{
                    background: "rgba(234,179,8,.14)", color: "var(--wd-yellow)",
                    border: "1px solid rgba(234,179,8,.35)", fontWeight: 600, fontSize: 12,
                  }}
                  onClick={() =>
                    navigate("/admin/claims", {
                      state: {
                        highlightCase: caseInfo?.accidentCaseNo,
                        autoOpenModal: "claimNo",
                      },
                    })
                  }
                >
                  + Add Claim Number Now →
                </button>
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  style={{ fontSize: 12, color: "var(--wd-t3)" }}
                  onClick={() => setClaimNoDeferred(true)}
                >
                  🕐 I'll add it later
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Deferred state — collapsed reminder */
          <div style={{
            padding: "12px 16px", borderRadius: 12,
            background: "rgba(249,115,22,.06)", border: "1px solid rgba(249,115,22,.25)",
            display: "flex", alignItems: "center", gap: 12,
          }}>
            <span style={{ fontSize: 18, flexShrink: 0 }}>⏰</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-orange)", marginBottom: 2 }}>
                Claim number pending — don't forget!
              </div>
              <div style={{ fontSize: 11, color: "var(--wd-t3)", lineHeight: 1.5 }}>
                This case won't progress on the Claims page until a claim number is entered.
                Add it as soon as the insurance company sends it.
              </div>
            </div>
            <button
              className="wd-btn wd-btn-sm"
              style={{
                flexShrink: 0,
                background: "rgba(249,115,22,.12)", color: "var(--wd-orange)",
                border: "1px solid rgba(249,115,22,.3)", fontWeight: 600, fontSize: 11,
              }}
              onClick={() =>
                navigate("/admin/claims", {
                  state: {
                    highlightCase: caseInfo?.accidentCaseNo,
                    autoOpenModal: "claimNo",
                  },
                })
              }
            >
              + Add Now
            </button>
          </div>
        )
        )}
 
        {/* Next step — assign surveyor */}
        {!surveySkipped && intimationHandled && !surveyorAssigned && !view && (
          <WaitingForSurveyor caseId={caseInfo?.accidentCaseNo} onSkipSurveys={handleSkipSurveys} skipSaving={saving} />
        )}

          {/* Spot survey status after surveyor assigned */}
        {surveyorAssigned && (
          <SpotSurveyStatus surveys={surveys} onGoToSurveys={onGoToSurveys} />
        )}
 
        <InsuranceInfoCard caseInfo={caseInfo} insuranceDetails={insuranceDetails} />
        
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Main render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Insurance summary */}
      <InsuranceInfoCard caseInfo={caseInfo} insuranceDetails={insuranceDetails} />

      {surveySkipped && (
        <SurveysSkippedBanner reason={surveySkipReason} />
      )}

        {/* ── Persistent warning: intimation raised but no claim number yet ── */}
    {!surveySkipped && alreadyRaised && !caseInfo?.claimNumber && !insuranceDetails?.claimNo && !view && (

      <div style={{
        padding: "12px 16px", borderRadius: 12,
        background: "rgba(249,115,22,.06)", border: "1px solid rgba(249,115,22,.25)",
        display: "flex", alignItems: "center", gap: 12,
      }}>
         <span style={{ fontSize: 18, flexShrink: 0 }}>⏰</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-orange)", marginBottom: 2 }}>
        Claim number pending — don't forget!
      </div>
      <div style={{ fontSize: 11, color: "var(--wd-t3)", lineHeight: 1.5 }}>
        This case won't progress on the Claims page until a claim number is entered.
        Add it as soon as the insurance company sends it.
      </div>
    </div>
    <button
      className="wd-btn wd-btn-sm"
      style={{
        flexShrink: 0,
        background: "rgba(249,115,22,.12)", color: "var(--wd-orange)",
        border: "1px solid rgba(249,115,22,.3)", fontWeight: 600, fontSize: 11,
      }}
      onClick={() => navigate("/admin/claims", {
        state: { highlightCase: caseInfo?.accidentCaseNo, autoOpenModal: "claimNo" },
      })}
    >
      + Add Now
    </button>

      </div>
    )}

      {/* If already raised and no surveyor yet — show waiting panel persistently */}
      {!surveySkipped && intimationHandled && !surveyorAssigned && !view && (
        <WaitingForSurveyor caseId={caseInfo?.accidentCaseNo} onSkipSurveys={handleSkipSurveys} skipSaving={saving} />
      )}

      {/* Surveyor assigned — show spot survey status */}
      {!surveySkipped && surveyorAssigned && !view && (
        <SpotSurveyStatus surveys={surveys} onGoToSurveys={onGoToSurveys} />
      )}

      {/* Intimation section */}
      <SectionCard
        title="Claim Intimation"
        action={
          view && view !== "choose"
            ? <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setView("choose")}>← Back</button>
            : null
        }
      >

        {/* ── Default view ──────────────────────────────────────────────────── */}
        {!view && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {intimationSkipped ? (
              <div style={{
                padding: "14px 18px", borderRadius: 12,
                background: "rgba(100,116,139,.08)", border: "1px solid rgba(100,116,139,.2)",
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--wd-t2)" }}>Intimation Skipped</div>
                <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 4 }}>{intimationSkipReason}</div>
              </div>
            ) : (
              <StatusBanner raised={alreadyRaised} date={intimationDate} method={intimationMode} />
            )}
            {!surveySkipped && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => setView("choose")}>
                  {intimationHandled ? "📋 Update Intimation" : "🔔 Raise Intimation"}
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Choose method ─────────────────────────────────────────────────── */}
        {view === "choose" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 13, color: "var(--wd-t2)" }}>
              How would you like to raise the intimation?
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <OptionCard
                icon="📋"
                title="Mark as Already Raised"
                desc="Already intimated the insurer by phone, portal, or in-person? Log it here to update the case status without sending an email."
                tag="Offline / Telephonic"
                tagColor="var(--wd-orange)"
                onClick={() => setView("direct")}
              />
              <OptionCard
                icon="📧"
                title="Send Intimation Email"
                desc="Generate a pre-filled intimation with all claim details and submitted documents. Review, edit if needed, then send to the insurance company."
                tag="Recommended"
                tagColor="var(--wd-blue)"
                onClick={openEmail}
              />
              <OptionCard
                icon="⏭️"
                title="Skip Intimation"
                desc="Not routing this claim through the insurer's intimation process? Record why and move on."
                tag="Requires reason"
                tagColor="var(--wd-t3)"
                onClick={() => setView("skipIntimation")}
              />
            </div>
          </div>
        )}

        {/* ── Direct / offline ──────────────────────────────────────────────── */}
        {view === "direct" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <div style={{
              padding: "10px 14px", borderRadius: 10, fontSize: 12,
              background: "rgba(249,115,22,.07)", border: "1px solid rgba(249,115,22,.2)",
              color: "var(--wd-orange)",
            }}>
              📋 Logging that intimation was already raised offline. Case status will move to <strong>INTIMATION_RAISED</strong>.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label className="wd-form-label">Method Used *</label>
                <select className="wd-input wd-select" value={directMethod} onChange={e => setDirectMethod(e.target.value)}>
                  <option value="TELEPHONIC">Telephonic</option>
                  <option value="PORTAL">Insurance Portal</option>
                  <option value="IN_PERSON">In Person</option>
                  <option value="FAX">Fax</option>
                </select>
              </div>
              <div>
                <label className="wd-form-label">Date of Intimation *</label>
                <input
                  className="wd-input" type="date"
                  value={directDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={e => setDirectDate(e.target.value)}
                />
              </div>
              <div>
                <label className="wd-form-label">Reference / Ticket No</label>
                <input
                  className="wd-input" type="text"
                  placeholder="e.g. HDFC-INTM-2024-78921"
                  value={directRef}
                  onChange={e => setDirectRef(e.target.value)}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, paddingTop: 22 }}>
                <input
                  type="checkbox" id="tp-direct" checked={directTP}
                  onChange={e => setDirectTP(e.target.checked)}
                  style={{ width: 15, height: 15, accentColor: "var(--wd-red)", cursor: "pointer" }}
                />
                <label htmlFor="tp-direct" style={{ fontSize: 13, color: "var(--wd-t1)", cursor: "pointer", userSelect: "none" }}>
                  Third Party Involved
                </label>
              </div>
            </div>

            <div>
              <label className="wd-form-label">Note</label>
              <textarea
                className="wd-input" rows={3} style={{ resize: "vertical", fontSize: 13 }}
                placeholder="e.g. Spoke to Mr. Sharma at HDFC claims, reference ticket noted…"
                value={directNote}
                onChange={e => setDirectNote(e.target.value)}
              />
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={reset} disabled={saving}>Cancel</button>
              <button
                className="wd-btn wd-btn-primary wd-btn-sm"
                onClick={handleDirectSave}
                disabled={saving || !directDate}
              >
                {saving ? "Saving…" : "✓ Save & Update Status"}
              </button>
            </div>
          </div>
        )}

        {/* ── Email form ─────────────────────────────────────────────────────── */}
        {view === "email" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            <div style={{
              padding: "10px 14px", borderRadius: 10, fontSize: 12,
              background: "rgba(59,130,246,.07)", border: "1px solid rgba(59,130,246,.2)",
              color: "var(--wd-blue)",
            }}>
              📧 Draft pre-filled with all claim details. Select documents to attach, edit if needed, then send. Status will move to <strong>INTIMATION_RAISED</strong> on send.
            </div>

            {/* To / CC / Subject */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label className="wd-form-label">To (Insurance Company) *</label>
                <input
                  className="wd-input" type="email"
                  placeholder="claims@insurancecompany.com"
                  value={emailTo}
                  onChange={e => setEmailTo(e.target.value)}
                />
              </div>
              <div>
                <label className="wd-form-label">CC</label>
                <input
                  className="wd-input" type="email"
                  placeholder="surveyor@example.com"
                  value={emailCc}
                  onChange={e => setEmailCc(e.target.value)}
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="wd-form-label">Subject *</label>
              <input
                className="wd-input" type="text"
                placeholder="Accident Claim Intimation — Policy No. ..."
                value={emailSubject}
                onChange={e => setEmailSubject(e.target.value)}
              />
            </div>

            {/* Third party toggle */}
            <label style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 14px", borderRadius: 10, cursor: "pointer",
              background: emailTP ? "rgba(239,68,68,.07)" : "var(--wd-hover)",
              border: `1px solid ${emailTP ? "rgba(239,68,68,.25)" : "var(--wd-border)"}`,
              userSelect: "none", transition: "all .12s",
            }}>
              <input
                type="checkbox" checked={emailTP}
                onChange={e => { setEmailTP(e.target.checked); refreshDraft(e.target.checked); }}
                style={{ width: 15, height: 15, accentColor: "var(--wd-red)", cursor: "pointer" }}
              />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>Third Party Loss Involved</div>
                <div style={{ fontSize: 11, color: "var(--wd-t3)" }}>Check if there is third party property damage or bodily injury. This updates the draft.</div>
              </div>
              {emailTP && (
                <span style={{
                  marginLeft: "auto", fontSize: 11, fontWeight: 600,
                  padding: "2px 8px", borderRadius: 20,
                  background: "rgba(239,68,68,.12)", color: "var(--wd-red)",
                }}>YES — Third Party</span>
              )}
            </label>

            {/* Document selection */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <label className="wd-form-label" style={{ margin: 0 }}>
                  Documents to Attach
                  <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 400, color: "var(--wd-t3)" }}>
                    ({selectedCount} of {docs.length} selected)
                  </span>
                </label>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }}
                    onClick={() => setSelectedDocs(Object.fromEntries(docs.map(d => [d.id, true])))}
                  >Select All</button>
                  <button
                    className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }}
                    onClick={() => refreshDraft()}
                  >↺ Refresh Draft</button>
                </div>
              </div>
              {docs.length === 0 ? (
                <div style={{ padding: 14, borderRadius: 10, background: "var(--wd-hover)", fontSize: 13, color: "var(--wd-t3)", textAlign: "center" }}>
                  No documents uploaded for this case yet.
                </div>
              ) : (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {docs.map(doc => (
                    <DocRow key={doc.id} doc={doc} checked={!!selectedDocs[doc.id]} onChange={toggleDoc} />
                  ))}
                </div>
              )}
            </div>

            {/* Email body */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                <label className="wd-form-label" style={{ margin: 0 }}>Email Body</label>
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 11 }}
                  onClick={() => refreshDraft()}
                >↺ Reset to Default</button>
              </div>
              <textarea
                className="wd-input"
                rows={20}
                style={{ resize: "vertical", fontSize: 12, fontFamily: "var(--wd-mono)", lineHeight: 1.75 }}
                value={emailBody}
                onChange={e => setEmailBody(e.target.value)}
              />
            </div>

            {/* Footer */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              paddingTop: 4, borderTop: "1px solid var(--wd-border)",
            }}>
              <div style={{ fontSize: 12, color: "var(--wd-t3)" }}>
                {selectedCount > 0
                  ? `📎 ${selectedCount} document${selectedCount !== 1 ? "s" : ""} will be attached`
                  : "⚠️ No documents selected"}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={reset} disabled={saving}>Cancel</button>
                <button
                  className="wd-btn wd-btn-primary wd-btn-sm"
                  onClick={handleSendEmail}
                  disabled={saving || !emailTo.trim() || !emailBody.trim()}
                  style={{ display: "flex", alignItems: "center", gap: 6 }}
                >
                  {saving ? "Sending…" : <><span>📤</span> Send Intimation</>}
                </button>
              </div>
            </div>
          </div>
        )}
        {view === "skipIntimation" && (
          <SkipReasonForm
            warningText="This marks the intimation step as intentionally skipped. You can still assign a spot surveyor afterward if needed."
            confirmLabel="Confirm Skip"
            saving={saving}
            onCancel={reset}
            onConfirm={handleSkipIntimation}
          />
)}
      </SectionCard>
    </div>
  );
}

// ── Surveys Tab ───────────────────────────────────────────────────────────────
// ── Surveys Tab ───────────────────────────────────────────────────────────────
export function SurveysTab({ surveys, caseInfo, workshop, services, onGoToWorkshop, onGoToServices, onRefetch }) {
  const list = surveys || [];
  const surveySkipped = !!caseInfo?.surveySkipped;   // CHANGED — no longer an early-return gate

  const isBeforeSurvey = stageIndex(caseInfo.stage) < stageIndex("SURVEY_PROCESS");

  // Split into spot and workshop survey
  const spotSurvey     = list.find(s => (s.type || "").toUpperCase().includes("SPOT") || s.type === "SPOT_SURVEY");
  const workshopSurvey = list.find(s => s.type === "WORKSHOP_SURVEY" || (s.type || "").toUpperCase().includes("WORKSHOP"));
  const otherSurveys   = list.filter(s => s !== spotSurvey && s !== workshopSurvey);

  const spotDone     = spotSurvey?.status === "COMPLETED" || !!spotSurvey?.completedAt;
  const workshopDone = workshopSurvey?.status === "COMPLETED" || !!workshopSurvey?.completedAt;

  // ADDED — only show the "nothing to do" skipped banner if there's truly
  // nothing else happening yet: no workshop assigned, no workshop survey,
  // no other surveys. Once a workshop or workshop survey exists, fall through
  // to the normal render below so that flow can proceed.
  const nothingElseYet = !workshop?.workshopId && !workshop?.primeMoverWorkshopId
    && !workshop?.trailerWorkshopId && !workshopSurvey && otherSurveys.length === 0;

  if (surveySkipped && nothingElseYet) {
    return (
      <div className="wd-card" style={{ padding: 24 }}>
        <SurveysSkippedBanner reason={caseInfo.surveySkipReason} />
      </div>
    );
  }

  // CHANGED — only show the classic empty state if surveys weren't skipped;
  // once skipped, we always want to fall through to the section-by-section
  // render below (even if `list` is still empty, e.g. workshop survey not
  // assigned yet) rather than showing the old "no surveyor assigned" message.
  if (list.length === 0 && !surveySkipped) {
    return (
      <div className="wd-card" style={{ padding:32 }}>
       <PendingSection
          icon="🔍"
          message="No surveyor assigned yet"
          subtext={isBeforeSurvey
            ? "Surveyor assignment is available after documents are submitted."
            : "Documents submitted. You can now assign a spot surveyor from the Surveyors page."}
          badge={isBeforeSurvey ? "Waiting for documents" : "Ready to assign"}
          badgeColor={isBeforeSurvey ? "var(--wd-orange)" : "var(--wd-blue)"}
        />
      </div>
    );
  }

  const surveyBadge = (s) => ({ COMPLETED:"green", ACCEPTED:"blue", ASSIGNED:"blue", REJECTED:"red", CANCELLED:"red", RESCHEDULED:"yellow" }[s] || "gray");

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>

      {/* ADDED — persistent note that the spot-survey stage was skipped,
          shown above whatever else is relevant (workshop survey etc.) */}
      {surveySkipped && (
        <SurveysSkippedBanner reason={caseInfo.surveySkipReason} />
      )}

       {/* ── Spot Survey ───────────────────────────────────────────────────── */}
      {/* CHANGED — only render the spot-survey section if it wasn't skipped */}
      {!surveySkipped && spotSurvey && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".06em", color: "var(--wd-t3)",
          }}>
            Spot Survey
          </div>
          <SurveyCard
            survey={spotSurvey}
            index={0}
            showActions={!spotDone}
            onRefetch={onRefetch}
          />

         {/* Next steps — only show once spot survey is done */}
          {spotDone && (
            <AfterSpotSurveyPanel
              services={services}
              caseInfo={caseInfo}
              workshop={workshop}
              onGoToWorkshop={onGoToWorkshop}
              onGoToServices={onGoToServices}
              onRefetch={onRefetch}
            />
          )}
        </div>
      )}

        {/* ── Workshop / Final Survey ───────────────────────────────────────── */}
      {/* UNCHANGED — this must always be reachable regardless of surveySkipped,
          since it's a separate, later stage in the workflow */}
      {workshop?.workshopId && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".06em", color: "var(--wd-t3)",
          }}>
            Workshop Survey
          </div>

          {workshopSurvey ? (
            <SurveyCard
              survey={workshopSurvey}
              index={0}
              showActions={!workshopDone}   // admin OR workshop can mark done
              onRefetch={onRefetch}
            />
          ) : (
            <WorkshopSurveyPanel workshopSurvey={null} caseInfo={caseInfo} />
          )}

          {/* If workshop survey exists but not done — also show the assign nudge */}
          {workshopSurvey && !workshopDone && (
            <WorkshopSurveyPanel workshopSurvey={workshopSurvey} caseInfo={caseInfo} />
          )}
        </div>
      )}

      {/* ── Any other survey types ────────────────────────────────────────── */}
      {otherSurveys.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{
            fontSize: 11, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".06em", color: "var(--wd-t3)",
          }}>
            Other Surveys
          </div>
          {otherSurveys.map((s, i) => (
            <SurveyCard
              key={s.id}
              survey={s}
              index={i}
              showActions={s.status !== "COMPLETED"}
              onRefetch={onRefetch}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ──────On-Spot Services Tab───────────────────────────────────────────────────────────────────

export function OnSpotServicesTab({ services: initialServices, caseInfo, linkedServices = [], accidentDetails, onRefetch }) {
  const updateServices = useUpdateOnSpotServices();
  // Customer pre-selected types
  const customerTypes = (initialServices || []).map(s => s.type);
 
  // Local service list — seeded from customer selection
  const [serviceList, setServiceList] = useState(() => {
    const base = (initialServices || []).map(s => ({
      id:           s.id || `svc-${s.type}`,
      type:         s.type,
      status:       s.status       || "PENDING",
      providerName: s.providerName || "",
      scheduledAt:  s.scheduledAt  || "",
      cost:         s.cost         || "",
      notes:        s.notes        || "",
      fromCustomer: true,
    }));
    return base;
  });
 
  // Which types are currently active
  const activeTypes = serviceList.map(s => s.type);
 
  // Dropdown / selector open state
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [pendingTypes,  setPendingTypes] = useState(activeTypes);
  const [saving, setSaving] = useState(false);
  const [launchType, setLaunchType] = useState(null);

  const linkedTypes = SERVICE_CATALOG
    .map(s => s.type)
    .filter(t => findLinked(t, linkedServices));

  useEffect(() => {
  if (!initialServices?.length) return;
  setServiceList(
    initialServices.map(s => ({
      id:           s.id || `svc-${s.type}`,
      type:         s.type,
      status:       s.status       || "PENDING",
      providerName: s.providerName || "",
      scheduledAt:  s.scheduledAt  || "",
      cost:         s.cost         || "",
      notes:        s.notes        || "",
      fromCustomer: true,
    }))
  );
}, [initialServices]);
 
  const openSelector = () => {
    setPendingTypes(activeTypes);
    setSelectorOpen(true);
  };
 
const applySelection = useCallback(async () => {
  setSaving(true);

  const finalTypes = [...new Set([...pendingTypes, ...linkedTypes])];

  try {
    await updateServices.mutateAsync({
      caseId: caseInfo.accidentCaseNo,
      services: finalTypes,    // ★ CHANGED: was pendingTypes
    });

    setServiceList(prev => {
      const kept = prev.filter(s => finalTypes.includes(s.type));          // ★ CHANGED: was pendingTypes
      const existingTypes = kept.map(s => s.type);
      const added = finalTypes                                              // ★ CHANGED: was pendingTypes
        .filter(t => !existingTypes.includes(t))
        .map(t => ({
          id: `svc-${t}-${Date.now()}`,
          type: t,
          status: "PENDING",
          providerName: "", scheduledAt: "", cost: "", notes: "",
          fromCustomer: false,
        }));
      return [...kept, ...added];
    });
 
    setSelectorOpen(false);
    onRefetch?.();
  } catch (e) {
    console.error("Failed to save services:", e);
  }
  setSaving(false);
  // ★ CHANGED: linkedTypes added to the dependency list
}, [pendingTypes, linkedTypes, caseInfo, updateServices, onRefetch]);

  const handleUpdate = useCallback((updated) => {
    setServiceList(prev => prev.map(s => s.type === updated.type ? updated : s));
    // 🔌 optionally call onRefetch here too
  }, []);

  const handleRemove = useCallback((type) => {
    setServiceList(prev => prev.filter(s => s.type !== type));
    // 🔌 await removeService.mutateAsync({ caseId: caseInfo.accidentCaseNo, type });
  }, []);

  const hasServices = serviceList.length > 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Header card */}
      <div className="wd-card" style={{ padding: "14px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>
              On-Spot Services
            </div>
            <div style={{ fontSize: 12, color: "var(--wd-t3)", marginTop: 2 }}>
              {customerTypes.length > 0
                ? `Customer pre-selected ${customerTypes.length} service${customerTypes.length > 1 ? "s" : ""} — pre-filled below`
                : "No services pre-selected by customer. Admin can add services manually."}
            </div>
          </div>
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            onClick={openSelector}
            style={{ whiteSpace: "nowrap" }}
          >
            {hasServices ? "✏️ Edit Services" : "＋ Add Services"}
          </button>
        </div>
 
        {/* Customer pre-selection pills */}
        {customerTypes.length > 0 && (
          <div style={{
            marginTop: 12, paddingTop: 12,
            borderTop: "1px solid var(--wd-border)",
            display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
          }}>
            <span style={{ fontSize: 11, color: "var(--wd-t3)", fontWeight: 600 }}>Customer selected:</span>
            {customerTypes.map(t => {
              const cat = SERVICE_CATALOG.find(s => s.type === t);
              return (
                <span key={t} style={{
                  fontSize: 11, fontWeight: 600, padding: "2px 10px", borderRadius: 20,
                  background: "rgba(168,85,247,.1)", color: "var(--wd-purple, #a855f7)",
                  border: "1px solid rgba(168,85,247,.22)",
                }}>
                  {cat?.icon} {cat?.label || t}
                </span>
              );
            })}
          </div>
        )}
      </div>
 
      {/* Selector panel */}
      {selectorOpen && (
        <div className="wd-card" style={{ padding: 18 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: "var(--wd-t1)",
            marginBottom: 12,
          }}>
            Select Services
          </div>
          <ServiceSelector
            selected={pendingTypes}
            onChange={setPendingTypes}
            customerSelected={customerTypes}
          />

          {linkedTypes.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 11, color: "var(--wd-t3)", lineHeight: 1.5 }}>
              ℹ️ Services that already have a request ({linkedTypes
                .map(t => SERVICE_CATALOG.find(s => s.type === t)?.label || t)
                .join(", ")}) can't be removed here. Cancel the request from its own page first.
            </div>
          )}
          <div style={{
            display: "flex", gap: 8, justifyContent: "flex-end",
            marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--wd-border)",
          }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => setSelectorOpen(false)} disabled={saving}>
              Cancel
            </button>
            <button
              className="wd-btn wd-btn-primary wd-btn-sm"
              onClick={applySelection}
              disabled={saving}
            >
              {saving ? "Saving…" : `Apply (${pendingTypes.length} selected)`}
            </button>
          </div>
        </div>
      )}
 
      {/* Summary stats */}
      {hasServices && !selectorOpen && (
        <ServicesSummary serviceList={serviceList} linkedServices={linkedServices} />
      )}
 
      {/* Service cards */}
      {hasServices && !selectorOpen && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {serviceList.map(svc => (
            <ServiceCard
              key={svc.type}
              service={svc}
              linked={findLinked(svc.type, linkedServices)}
              onCreateRequest={() => setLaunchType(svc.type)}
              onUpdate={handleUpdate}
              onRemove={handleRemove}
            />
          ))}
        </div>
      )}
 
      {/* Empty state */}
      {!hasServices && !selectorOpen && (
        <div className="wd-card" style={{ padding: 32 }}>
          <PendingSection
            icon="🛠️"
            message="No on-spot services added yet"
            subtext="Customer did not select any services. You can add them manually using the button above."
            badge="Optional"
            badgeColor="var(--wd-purple, #a855f7)"
          />
        </div>
      )}
      {launchType && (
        <LinkedServiceLauncher
          type={launchType}
          caseInfo={caseInfo}
          accidentDetails={accidentDetails}
          onClose={() => setLaunchType(null)}
          onCreated={() => { setLaunchType(null); onRefetch?.(); }}
        />
      )}
 
    </div>
  );
}
  const roleColor = { ADMIN:"var(--wd-blue)", CUSTOMER:"var(--wd-green)", SURVEYOR:"var(--wd-purple)", WORKSHOP:"var(--wd-orange)", SYSTEM:"var(--wd-t3)" };


// ── Timeline Tab ──────────────────────────────────────────────────────────────
export function TimelineTab({ timeline, caseInfo }) {
  const list = timeline || [];
  const timelineStatusLabels = {
    PART_ORDERED: "PARTS ORDERED",
    CLAIM_SUBMITTED: "CLAIM FORM SUBMITTED",
    PART_DELIVERED: "PARTS DELIVERED",
    WORKSHOP_SURVEY_ASSIGNED: "WORKSHOP 1ST SURVEY ASSIGNED",
    WORKSHOP_SURVEY_COMPLETED: "WORKSHOP 1ST SURVEY COMPLETED",
    WORKSHOP_ESTIMATE: "WAITING FOR ESTIMATE COPY",
  };
  // caseInfo may be the full detail object { caseInfo: {...}, ... } or a plain caseInfo
  const caseId = caseInfo?.caseInfo?.accidentCaseNo || caseInfo?.accidentCaseNo || null;

  const timelineEl = list.length === 0
    ? <div className="wd-card" style={{ padding: 32 }}><EmptyState message="No timeline events yet" /></div>
    : (
      <div className="wd-card" style={{ padding: 24 }}>
        {[...list].reverse().map((e, i) => {
          const color = roleColor[e.role || e.updatedByRole] || "var(--wd-t3)";
          return (
            <div key={e.id || i} style={{ display:"flex", gap:14 }}>
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:color, flexShrink:0, marginTop:4 }} />
                {i < list.length-1 && <div className="wd-tl-line" />}
              </div>
              <div style={{ paddingBottom:20 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                  <span style={{ fontSize:13, fontWeight:600, color:"var(--wd-t1)" }}>
                    {timelineStatusLabels[e.status || e.eventType] ||
                      (e.status||e.eventType||"").replace(/_/g," ")}
                  </span>
                  <span style={{ fontSize:11, padding:"1px 7px", borderRadius:10, background:`${color}22`, color, fontWeight:600 }}>
                    {e.role || e.updatedByRole}
                  </span>
                </div>
                {e.description && <p style={{ margin:"3px 0 0", fontSize:13, color:"var(--wd-t2)" }}>{e.description}</p>}
                <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:3 }}>
                  {e.actor || e.updatedById || e.role} · {formatDateTime(e.timestamp || e.createdAt)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );

  return (
    <div style={{ display: "grid", gridTemplateColumns: caseId ? "1fr 340px" : "1fr", gap: 16, alignItems: "start" }}>
      {timelineEl}
      {caseId && <CaseSLAPanel caseId={caseId} />}
    </div>
  );
}

function normalizeEvent(t) {
  return {
    step: t.status || t.eventType,
    role: t.role || t.updatedByRole,
    actor: t.actor || t.updatedById || t.role,
    description: t.description,
    createdAt: t.timestamp || t.createdAt,
  };
}

function buildStepGroups(timeline, dailyUpdates) {
  const noteMap = new Map();
  (dailyUpdates || []).forEach(u => {
    if (!noteMap.has(u.caseStep)) noteMap.set(u.caseStep, []);
    noteMap.get(u.caseStep).push(u);
  });

  const byTime = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

  const groupMap = new Map(); // step -> { step, events: [], notes: [] }

  (timeline || []).forEach(raw => {
    const ev = normalizeEvent(raw);
    if (!ev.step) return; // still no usable step — skip rather than show blank
    if (!groupMap.has(ev.step)) {
      groupMap.set(ev.step, { step: ev.step, events: [], notes: noteMap.get(ev.step) || [] });
    }
    groupMap.get(ev.step).events.push(ev);
  });

  for (const [step, notes] of noteMap.entries()) {
    if (!groupMap.has(step)) {
      groupMap.set(step, { step, events: [], notes });
    }
  }

  const groups = Array.from(groupMap.values()).map(g => ({
    ...g,
    events: g.events.sort(byTime),
    notes: g.notes.slice().sort(byTime),
  }));

  groups.sort((a, b) => {
    const at = a.events[0]?.createdAt || a.notes[0]?.createdAt || 0;
    const bt = b.events[0]?.createdAt || b.notes[0]?.createdAt || 0;
    return new Date(bt) - new Date(at); // newest step first
  });

  return groups;
}

function NoteEditForm({ initialValue, onSave, onCancel, saving }) {
  const [val, setVal] = useState(initialValue);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
      <textarea
        className="wd-input"
        rows={2}
        style={{ resize: "vertical", fontSize: 13 }}
        value={val}
        onChange={e => setVal(e.target.value)}
      />
      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
        <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
        <button
          className="wd-btn wd-btn-primary wd-btn-sm"
          onClick={() => onSave(val)}
          disabled={saving || !val.trim() || val.trim() === initialValue}
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}

// Read-only — this is the actual status-change event, never editable.
function EventRow({ event, isLast }) {
  const color = roleColor[event.role] || "var(--wd-t3)";
  return (
    <div style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: isLast ? "none" : "1px solid var(--wd-border)" }}>
      <div style={{
        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
        background: "var(--wd-hover)", border: `1px solid ${color}55`,
        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12,
      }}>
        🕓
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-t1)" }}>{event.actor || "System"}</span>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 10,
            background: `${color}22`, color, textTransform: "uppercase",
          }}>
            {event.role || "SYSTEM"}
          </span>
          <span style={{
            fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 10,
            background: "rgba(148,163,184,.1)", color: "var(--wd-t3)", textTransform: "uppercase",
          }}>
            Step reached
          </span>
          <span style={{ fontSize: 11, color: "var(--wd-t3)", marginLeft: "auto", flexShrink: 0 }}>
            {formatDateTime(event.createdAt)}
          </span>
        </div>
        {event.description && (
          <p style={{ margin: 0, fontSize: 13, color: "var(--wd-t2)", lineHeight: 1.6 }}>{event.description}</p>
        )}
      </div>
    </div>
  );
}

function NoteRow({ note, caseId, isLast, onEdited }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const editUpdate = useEditDailyUpdate(caseId);
  const color = roleColor[note.createdByRole] || "var(--wd-t3)";

  const handleSave = async (val) => {
    setSaving(true);
    try {
      await editUpdate.mutateAsync({ caseId, updateId: note.id, note: val.trim() });
      setEditing(false);
      onEdited?.();
    } catch (err) {
      console.error(err);
    }
    setSaving(false);
  };

  return (
    <div style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: isLast ? "none" : "1px solid var(--wd-border)" }}>
      <Avatar name={note.createdById || "?"} size={26} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: "var(--wd-t1)" }}>{note.createdById || "Admin"}</span>
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 10,
            background: `${color}22`, color, textTransform: "uppercase",
          }}>
            {note.createdByRole}
          </span>
          {note.updatedAt && !editing && (
            <span style={{ fontSize: 10, color: "var(--wd-t3)", fontStyle: "italic" }}>edited</span>
          )}
          <span style={{ fontSize: 11, color: "var(--wd-t3)", marginLeft: "auto", flexShrink: 0 }}>
            {formatDateTime(note.createdAt)}
          </span>
          {!editing && (
            <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 10, padding: "1px 6px" }} onClick={() => setEditing(true)}>
              ✏️
            </button>
          )}
        </div>
        {editing ? (
          <NoteEditForm initialValue={note.note} onSave={handleSave} onCancel={() => setEditing(false)} saving={saving} />
        ) : (
          <p style={{ margin: 0, fontSize: 13, color: "var(--wd-t2)", lineHeight: 1.6 }}>{note.note}</p>
        )}
      </div>
    </div>
  );
}

function StepGroup({ step, events, notes, isCurrent, expanded, onToggle, caseId, onEdited }) {
  const variant = statusBadge(step);
  const latest = notes.at(-1)?.createdAt || events.at(-1)?.createdAt;
  const rows = [...events.map(e => ({ type: "event", data: e })), ...notes.map(n => ({ type: "note", data: n }))]
    .sort((a, b) => new Date(b.data.createdAt) - new Date(a.data.createdAt)); // newest first, event (oldest) sinks to bottom

  return (
    <div className="wd-card" style={{
      padding: 0, overflow: "hidden",
      border: isCurrent ? "1px solid var(--wd-blue)" : "1px solid var(--wd-border)",
    }}>
      <button
        onClick={onToggle}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 10,
          padding: "12px 16px", textAlign: "left", flexWrap: "wrap",
        }}
      >
        <Badge variant={variant}>{STATUS_LABELS[step] || (step || "").replace(/_/g, " ")}</Badge>
        {isCurrent && (
          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--wd-blue)", textTransform: "uppercase" }}>
            Current step
          </span>
        )}
        {notes.length > 0 && (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "1px 8px", borderRadius: 10,
            background: "var(--wd-hover)", color: "var(--wd-t3)",
          }}>
            {notes.length} update{notes.length > 1 ? "s" : ""}
          </span>
        )}
        <span style={{ fontSize: 11, color: "var(--wd-t3)", marginLeft: "auto" }}>
          {latest ? formatDateTime(latest) : ""}
        </span>
        <span style={{
          fontSize: 11, color: "var(--wd-t3)",
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform .15s",
        }}>▾</span>
      </button>

      {expanded && (
        <div style={{ padding: "0 16px 4px", borderTop: "1px solid var(--wd-border)" }}>
          {rows.map((r, i) =>
            r.type === "event" ? (
              <EventRow key={`ev-${i}`} event={r.data} isLast={i === rows.length - 1} />
            ) : (
              <NoteRow key={`n-${r.data.id}`} note={r.data} caseId={caseId} isLast={i === rows.length - 1} onEdited={onEdited} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function AddUpdateBox({ currentStatus, onSubmit }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!note.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSubmit(note.trim());
      setNote("");
      setOpen(false);
    } catch (err) {
      console.error(err);
      setError("Failed to save update. Please retry.");
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <button
        className="wd-card"
        onClick={() => setOpen(true)}
        style={{
          padding: "12px 16px", textAlign: "left", cursor: "pointer",
          display: "flex", alignItems: "center", gap: 10,
          border: "1px dashed var(--wd-border)", background: "var(--wd-hover)",
        }}
      >
        <span style={{ fontSize: 16, color: "var(--wd-blue)" }}>＋</span>
        <span style={{ fontSize: 13, color: "var(--wd-t3)" }}>Add a daily update…</span>
        <span style={{ marginLeft: "auto" }}>
          <Badge variant={statusBadge(currentStatus)}>{STATUS_LABELS[currentStatus] || currentStatus}</Badge>
        </span>
      </button>
    );
  }

  return (
    <SectionCard
      title="Add Daily Update"
      action={<Badge variant={statusBadge(currentStatus)}>{STATUS_LABELS[currentStatus] || currentStatus}</Badge>}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <textarea
          className="wd-input"
          rows={3}
          autoFocus
          style={{ resize: "vertical", fontSize: 13 }}
          placeholder="e.g. All documents shared with surveyor, dismantling approval still pending…"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
        {error && <div style={{ fontSize: 12, color: "var(--wd-red)" }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => { setOpen(false); setNote(""); setError(null); }} disabled={saving}>
            Cancel
          </button>
          <button
            className="wd-btn wd-btn-primary wd-btn-sm"
            onClick={handleSubmit}
            disabled={saving || !note.trim()}
          >
            {saving ? "Saving…" : "＋ Add Update"}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

export function DailyUpdateTab({ caseId, currentStatus, timeline }) {
  const { data: dailyUpdates, isLoading, refetch } = useDailyUpdates(caseId);
  const addUpdate = useAddDailyUpdate(caseId);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const [seeded, setSeeded] = useState(false);

  const groups = buildStepGroups(timeline, dailyUpdates);

  useEffect(() => {
    if (!seeded && groups.length) {
      const initial = groups.find(g => g.step === currentStatus)?.step || groups[0].step;
      setExpandedSteps(new Set([initial]));
      setSeeded(true);
    }
  }, [seeded, groups, currentStatus]);

  const toggleGroup = (step) => {
    setExpandedSteps(prev => {
      const next = new Set(prev);
      next.has(step) ? next.delete(step) : next.add(step);
      return next;
    });
  };

  const allExpanded = groups.length > 0 && groups.every(g => expandedSteps.has(g.step));
  const toggleAll = () => {
    setExpandedSteps(allExpanded ? new Set() : new Set(groups.map(g => g.step)));
  };

  const handleAdd = async (noteText) => {
    await addUpdate.mutateAsync({ caseId, note: noteText });
    refetch();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <AddUpdateBox currentStatus={currentStatus} onSubmit={handleAdd} />

      {isLoading ? (
        <Spinner />
      ) : groups.length === 0 ? (
        <div className="wd-card" style={{ padding: 32 }}>
          <EmptyState message="No activity yet" />
        </div>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={toggleAll}>
              {allExpanded ? "Collapse all" : "Expand all"}
            </button>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {groups.map(g => (
              <StepGroup
                key={g.step}
                step={g.step}
                events={g.events}
                notes={g.notes}
                isCurrent={g.step === currentStatus}
                expanded={expandedSteps.has(g.step)}
                onToggle={() => toggleGroup(g.step)}
                caseId={caseId}
                onEdited={refetch}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Reusable Pending State ────────────────────────────────────────────────────
export function PendingSection({ icon, message, subtext, badge, badgeColor = "var(--wd-orange)" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:10, padding:"20px 0", textAlign:"center" }}>
      {icon && <span style={{ fontSize:36, opacity:.5 }}>{icon}</span>}
      <div style={{ fontSize:14, fontWeight:600, color:"var(--wd-t2)" }}>{message}</div>
      {subtext && <div style={{ fontSize:12, color:"var(--wd-t3)", maxWidth:360, lineHeight:1.6 }}>{subtext}</div>}
      {badge && (
        <span style={{ fontSize:11, fontWeight:600, padding:"3px 10px", borderRadius:20, background:`${badgeColor}18`, color:badgeColor, border:`1px solid ${badgeColor}33` }}>
          {badge}
        </span>
      )}
    </div>
  );
}
 
export function PendingTag() {
  return (
    <span style={{ fontSize:11, color:"var(--wd-orange)", background:"rgba(249,115,22,.1)", padding:"2px 8px", borderRadius:20, fontWeight:600 }}>
      Pending customer input
    </span>
  );
}